﻿import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Checkbox, Divider, Input, InputNumber, Menu, Modal, Popover, Popconfirm, Progress, Radio, Select, Space, Switch, Table, Tabs, Tag, Tooltip, Typography, Upload, message,
} from 'antd';
import {
  ApiOutlined, BookOutlined, CloudUploadOutlined, DownloadOutlined, FileZipOutlined, FolderOutlined, GlobalOutlined, PictureOutlined, UsbOutlined, VideoCameraOutlined, WindowsOutlined,
} from '@ant-design/icons';
import { useDeployStore } from '../../stores/deployStore';
import { useWatermarkFlag } from '../Watermark';
import useSceneStore from '../../stores/sceneStore';
import {
  CreateReleaseResponse, DeployActivationRecord, DeployArtifactPolicy, DeployPolicyView, DeploymentRecord,
  DataTokenInfo, DeliverRecord, IotPackageView, IotValidateReport, createDataToken, createDeployment,
  createRelease, deliverRelease, deployErrorOf, downloadReleaseFile, exportIotPackage, fetchDeployPolicy,
  getDataToken, getIotPackage, issueActivation, listActivations, listDeliveries, putDataScope,
  revokeActivation, revokeDataToken, setDeploymentLinkPassword, unpublishDeployment, updateDeployment,
  validateIotPackage, deliverModelPackage, listModelFiles, uploadModelFile,
  fetchViewerInstaller, fetchUsbPreflight, type UsbPreflightItem,
} from '../../api/deploy';
import { parseIotPointsCsv } from '../../utils/iotCsv';
import { fetchLicenseTiers, PayLicenseTier } from '../../api/pay';
import { usePayIntentStore } from '../payment/payIntent';
import OemEditor from './OemEditor';
import { exportDeployImage, downloadDataUrl } from '../../utils/deployImageExport';
import {
  GIF_MAX_DURATION_SEC, VIDEO_FPS_OPTIONS, downloadBlob, recordDeployVideo,
} from '../../utils/deployVideoExport';
import TierGateAlert from './TierGateAlert';

const { Text } = Typography;

const TIER_LABEL: Record<string, string> = {
  free: '免费版',
  basic: '基础版',
  pro: '专业版',
  enterprise: '尊享版',
};

const HELP_ITEMS: [string, string][] = [
  ['项目码', '对外授权凭据（DTw-XXXX-XXXX），绑定部署包/EXE 激活与发布记录'],
  ['实时更新 / 手动更新', '实时=跟随编辑器改动即时生效；手动=重新发布才更新（推荐用于交付）'],
  ['iframe 嵌入白名单', '仅允许名单内的域名将发布页嵌入到其网页；换行分隔多个域名'],
  ['访客模式', '开启后无需平台账号即可访问（默认）；关闭需登录平台账号'],
  ['项目 OEM', '替换导航图标/加载 LOGO/登录背景（素材上传在下一阶段开放）'],
  ['查看器发布设置', '基础=旋转缩放看数据；高级=漫游/第一人称/事件联动（随授权档位）'],
  ['高级数据源', 'SQL/MQTT/数据集代理直连（基础=静态/API/设备实时）'],
  ['出图倍率', '1080p / 2K / 4K，按套餐开放（管理员可在后台配置上限）'],
];

/** 每个 Tab 独立保存按钮（对齐参考图底部操作区） */

export default function ProjectDeployModal() {
  const { open, target, close } = useDeployStore();
  const [policy, setPolicy] = useState<DeployPolicyView | null>(null);
  const [tab, setTab] = useState('web');
  const [deployment, setDeployment] = useState<DeploymentRecord | null>(null);

  useEffect(() => {
    if (!open) return;
    setTab('web');
    setDeployment(null);
    fetchDeployPolicy()
      .then(setPolicy)
      .catch(() => setPolicy(null));
  }, [open]);

  const artifactOf = useCallback(
    (kind: string): DeployArtifactPolicy | undefined => policy?.artifacts?.[kind],
    [policy],
  );

  /** 幂等获取/创建部署项目（同来源复用） */
  const ensureDeployment = useCallback(async (): Promise<DeploymentRecord> => {
    if (deployment) return deployment;
    if (!target) throw new Error('缺少部署目标');
    const created = await createDeployment({
      type: target.kind,
      sceneId: target.sceneId,
      pageId: target.pageId,
      name: target.name,
    });
    setDeployment(created);
    return created;
  }, [deployment, target]);

  if (!target) return null;

  const helpContent = (
    <div style={{ maxWidth: 380 }}>
      {HELP_ITEMS.map(([k, v]) => (
        <div key={k} style={{ marginBottom: 8 }}>
          <Text strong style={{ fontSize: 13 }}>{k}</Text>
          <div style={{ fontSize: 12, color: '#999' }}>{v}</div>
        </div>
      ))}
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      width={980}
      title={
        <Space>
          <span>项目部署</span>
          {target.name ? <Tag>{target.name}</Tag> : null}
          {policy ? <Tag color="blue" data-testid="deploy-tier">{TIER_LABEL[policy.tier] ?? policy.tier}</Tag> : null}
        </Space>
      }
      styles={{ body: { padding: 0 } }}
      data-testid="project-deploy-modal"
    >
      <div style={{ display: 'flex', minHeight: 520 }}>
        {/* 左侧菜单 + 促销位（对齐参考图） */}
        <div style={{ width: 190, borderRight: '1px solid #303030', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <Menu
              mode="inline"
              selectedKeys={[tab]}
              style={{ borderInlineEnd: 'none', background: 'transparent' }}
              onClick={(e) => setTab(e.key)}
              items={[
                { key: 'web', icon: <GlobalOutlined />, label: '发布Web链接' },
                { key: 'package', icon: <FolderOutlined />, label: '导出部署文件' },
                { key: 'exe', icon: <WindowsOutlined />, label: '导出EXE文件' },
                { key: 'image', icon: <PictureOutlined />, label: '导出图片' },
                { key: 'video', icon: <VideoCameraOutlined />, label: '导出视频' },
                { key: 'asset', icon: <FileZipOutlined />, label: '导出资产包' },
                { key: 'usb', icon: <UsbOutlined />, label: '离线U盘包' },
                { key: 'edge', icon: <ApiOutlined />, label: '边缘与对接' },
              ]}
            />
          </div>
          <div style={{ padding: 12 }}>
            <div
              style={{
                borderRadius: 8,
                padding: '10px 12px',
                background: 'linear-gradient(135deg, #722ed1 0%, #1890ff 100%)',
                cursor: 'pointer',
              }}
              onClick={() => policy?.promo?.link && window.open(policy.promo.link, '_blank')}
              data-testid="deploy-promo"
            >
              <div style={{ color: '#fff', fontSize: 12, fontWeight: 600, lineHeight: '18px' }}>
                {policy?.promo?.text ?? '升级尊享版 导出部署文件无限制'}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, marginTop: 4 }}>立即查看 →</div>
            </div>
          </div>
        </div>

        {/* 右侧内容 */}
        <div style={{ flex: 1, padding: '16px 20px', overflow: 'auto', maxHeight: '72vh' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
            <Popover content={helpContent} title="发布教程 · 名词解释" trigger="click" placement="bottomRight">
              <Button size="small" type="text" icon={<BookOutlined />} data-testid="deploy-help">帮助</Button>
            </Popover>
          </div>

          {tab === 'web' && (
            <WebLinkTab
              target={target}
              policy={policy}
              artifact={artifactOf('web')}
              ensureDeployment={ensureDeployment}
              onDeploymentUpdate={setDeployment}
            />
          )}
          {tab === 'image' && (
            <ImageTab target={target} artifact={artifactOf('image')} policy={policy} ensureDeployment={ensureDeployment} />
          )}
          {tab === 'video' && (
            <VideoTab target={target} artifact={artifactOf('video')} policy={policy} ensureDeployment={ensureDeployment} />
          )}
          {tab === 'asset' && (
            <AssetTab target={target} artifact={artifactOf('asset')} policy={policy} ensureDeployment={ensureDeployment} />
          )}
          {tab === 'usb' && (
            <UsbTab artifact={artifactOf('usb')} policy={policy} ensureDeployment={ensureDeployment} />
          )}
          {tab === 'package' && (
            <PackageTab
              target={target}
              artifact={artifactOf('package')}
              policy={policy}
              ensureDeployment={ensureDeployment}
            />
          )}
          {tab === 'exe' && (
            <ExeTab
              target={target}
              artifact={artifactOf('exe')}
              policy={policy}
              ensureDeployment={ensureDeployment}
            />
          )}
          {tab === 'edge' && (
            <EdgeTab
              target={target}
              artifact={artifactOf('edge')}
              policy={policy}
              ensureDeployment={ensureDeployment}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

// ============================================================
// 发布 Web 链接（P0-6 接口全量对接）
// ============================================================

function WebLinkTab({
  target,
  policy,
  artifact,
  ensureDeployment,
  onDeploymentUpdate,
}: {
  target: { kind: string; name?: string };
  policy: DeployPolicyView | null;
  artifact?: DeployArtifactPolicy;
  ensureDeployment: () => Promise<DeploymentRecord>;
  onDeploymentUpdate: (d: DeploymentRecord) => void;
}) {
  const [appName, setAppName] = useState(target.name ? `${target.name}-数字孪生系统` : '数字孪生系统');
  const [showToolbar, setShowToolbar] = useState(true);
  const [updateMode, setUpdateMode] = useState<'manual' | 'live'>('manual');
  const [accessMode, setAccessMode] = useState<'direct' | 'iframe' | 'both'>('both');
  const [whitelist, setWhitelist] = useState('');
  const [alias, setAlias] = useState('');
  const [linkPassword, setLinkPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState<{ url: string; aliasUrl?: string } | null>(null);

  const gateOk = !!artifact?.allowed;

  const saveLinkPassword = async (password: string | null) => {
    setPwSaving(true);
    try {
      const dep = await ensureDeployment();
      const r = await setDeploymentLinkPassword(dep.id, password);
      setPasswordRequired(r.passwordRequired);
      setLinkPassword('');
      message.success(r.passwordRequired ? '访问密码已设置' : '已清除访问密码（公开访问）');
    } catch (e) {
      message.error(deployErrorOf(e).message);
    } finally {
      setPwSaving(false);
    }
  };

  const doPublish = async () => {
    setPublishing(true);
    try {
      const dep = await ensureDeployment();
      const updated = await updateDeployment(dep.id, {
        name: appName,
        alias: alias.trim() || null,
        settings: {
          showToolbar,
          updateMode,
          accessMode,
          iframeWhitelist: accessMode === 'direct' ? null : whitelist || null,
        },
      });
      onDeploymentUpdate(updated);
      const res: CreateReleaseResponse = await createRelease(dep.id, {
        kind: 'web',
        options: {
          showToolbar,
          updateMode,
          accessMode,
          iframeWhitelist: accessMode === 'direct' ? null : whitelist || null,
          staticize: true,
        },
      });
      if (res.release.status !== 'ready') throw new Error(`构建未完成（${res.release.status}）`);
      setResult({
        url: `${window.location.origin}${res.links.short}`,
        aliasUrl: alias.trim() ? `${window.location.origin}/v/${alias.trim()}` : undefined,
      });
      message.success('发布成功');
    } catch (e) {
      const err = deployErrorOf(e);
      // 别名冲突：自动填入建议别名，用户再次点击发布即可（幂等修复 DW 反馈）
      if (err.code === 'DEPLOY_ALIAS_TAKEN' && err.suggestion) {
        setAlias(err.suggestion);
        message.warning(`${err.message}（已自动填入建议别名，请再次点击发布）`);
      } else if (err.code === 'DEPLOY_TIER_REQUIRED' || err.code === 'DEPLOY_KIND_DISABLED' || err.code === 'DEPLOY_QUOTA_EXCEEDED') {
        Modal.warning({ title: '暂时无法发布', content: err.message, okText: '知道了' });
      } else {
        message.error(err.message);
      }
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div data-testid="deploy-web-tab">
      <Divider titlePlacement="start" plain style={{ marginTop: 0 }}>基础设置</Divider>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
        <Field label="应用名称" tip="发布后查看器标题/分享页名称；示例：现代物流模型-数字孪生系统">
          <Input value={appName} onChange={(e) => setAppName(e.target.value)} data-testid="deploy-app-name" />
        </Field>
        <Field label="是否显示工具栏" tip="关闭后访客仅有画面（推荐交付关闭）">
          <Switch checked={showToolbar} onChange={setShowToolbar} checkedChildren="显示工具栏" unCheckedChildren="关闭" />
        </Field>
        <Field label="更新同步" tip="实时=跟随编辑器改动；手动=重新发布才更新（推荐交付用手动）">
          <Radio.Group value={updateMode} onChange={(e) => setUpdateMode(e.target.value)}>
            <Radio value="manual">手动更新</Radio>
            <Radio value="live">实时更新</Radio>
          </Radio.Group>
        </Field>
        <Field label="访问安全" tip="iframe 嵌入需配置域名白名单（发布后生效）">
          <Radio.Group value={accessMode} onChange={(e) => setAccessMode(e.target.value)}>
            <Radio value="both">直接访问+iframe</Radio>
            <Radio value="direct">直接访问</Radio>
            <Radio value="iframe">iframe 嵌入</Radio>
          </Radio.Group>
        </Field>
        {accessMode !== 'direct' && (
          <Field label="嵌入域名白名单" tip="默认可嵌入所有域名；如需限制请输入域名，多个域名以换行分隔" span2>
            <Input.TextArea
              rows={2}
              value={whitelist}
              onChange={(e) => setWhitelist(e.target.value)}
              placeholder={'example.com\nsub.example.cn'}
              data-testid="deploy-iframe-whitelist"
            />
          </Field>
        )}
      </div>
      <Divider titlePlacement="start" plain>项目 OEM</Divider>
      <OemEditor ensureDeployment={ensureDeployment} />

      <Divider titlePlacement="start" plain>高级设置</Divider>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
        <Field label="自定义链接别名" tip="生成 /v/{别名} 短链（唯一），空=仅使用随机令牌链接">
          <Input value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="如 workshop-a" maxLength={32} data-testid="deploy-alias" />
        </Field>
        <Field label="访问密码（可选）" tip="留空=公开访问；设置后访客需输入密码（验证后 2 小时内免重复），可随时清除">
          <Space.Compact style={{ width: '100%' }}>
            <Input.Password
              value={linkPassword}
              onChange={(e) => setLinkPassword(e.target.value)}
              placeholder={passwordRequired ? '已启用（输入新密码可更换）' : '设置访问密码'}
              data-testid="deploy-link-password"
            />
            <Button
              onClick={() => void saveLinkPassword(linkPassword.trim())}
              loading={pwSaving}
              disabled={!linkPassword.trim()}
              data-testid="deploy-link-password-save"
            >
              保存
            </Button>
            <Button
              danger
              disabled={!passwordRequired}
              onClick={() => void saveLinkPassword(null)}
              data-testid="deploy-link-password-clear"
            >
              清除
            </Button>
          </Space.Compact>
          {passwordRequired ? (
            <div style={{ fontSize: 12, color: '#faad14', marginTop: 4 }}>当前链接已启用访问密码</div>
          ) : null}
        </Field>
        <Field label="APP 控制 / 云渲染" tip="规划中（敬请期待）">
          <Space>
            <Switch checked={false} disabled /> <Text type="secondary" style={{ fontSize: 12 }}>规划中</Text>
          </Space>
        </Field>
      </div>

      <TierGateAlert artifact={artifact} policy={policy} label="发布 Web 链接" testId="deploy-web-gate" />

      {result && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 12 }}
          data-testid="deploy-web-result"
          message="发布成功"
          description={
            <Space orientation="vertical" size={4} style={{ width: '100%' }}>
              {result.aliasUrl ? (
                <>
                  <Text type="secondary" style={{ fontSize: 12 }}>别名链接（推荐）：</Text>
                  <Text copyable style={{ color: '#7ecbff', wordBreak: 'break-all' }}>{result.aliasUrl}</Text>
                </>
              ) : null}
              <Text type="secondary" style={{ fontSize: 12 }}>{result.aliasUrl ? '令牌链接：' : ''}</Text>
              <Text copyable style={{ color: '#7ecbff', wordBreak: 'break-all' }}>{result.url}</Text>
              <Space>
                <Button size="small" onClick={() => window.open(result.aliasUrl || result.url, '_blank')}>打开链接</Button>
              </Space>
            </Space>
          }
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          icon={<CloudUploadOutlined />}
          loading={publishing}
          disabled={!gateOk}
          onClick={() => void doPublish()}
          data-testid="deploy-web-publish"
        >
          开始发布
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// 导出图片（DW-P0-7 服务 + image 轻量记录）
// ============================================================

function ImageTab({
  target,
  artifact,
  policy,
  ensureDeployment,
}: {
  target: { kind: 'scene' | 'dashboard' | 'fusion'; name?: string };
  artifact?: DeployArtifactPolicy;
  policy: DeployPolicyView | null;
  ensureDeployment: () => Promise<DeploymentRecord>;
}) {
  const [format, setFormat] = useState<'png' | 'jpg'>('png');
  const [scale, setScale] = useState<1 | 2 | 4>(1);
  const [exporting, setExporting] = useState(false);
  const wmFlag = useWatermarkFlag();
  const sceneName = useSceneStore((s) => s.currentScene?.name);
  const maxScale = Number(artifact?.maxScale) > 0 ? Number(artifact?.maxScale) : 4;
  const gateOk = !!artifact?.allowed;
  const showWatermark = wmFlag !== false;

  const doExport = async () => {
    setExporting(true);
    try {
      const kind = target.kind;
      const el =
        kind === 'scene'
          ? null
          : document.querySelector<HTMLElement>('[data-testid="dashboard-canvas"]');
      const res = await exportDeployImage(kind, el, {
        name: target.name || sceneName || '大屏',
        format,
        scale,
        watermark: showWatermark ? { text: 'DTwince', logoUrl: '/watermark-logo.png' } : null,
      });
      downloadDataUrl(res.dataUrl, res.filename);
      const dep = await ensureDeployment();
      await createRelease(dep.id, {
        kind: 'image',
        options: { format, width: res.width, height: res.height, scale, watermark: showWatermark },
      });
      message.success(`已导出 ${res.filename}${showWatermark ? '（含水印）' : ''}`);
    } catch (e) {
      message.error((e as Error).message || '导出失败');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="deploy-image-tab">
      <Divider titlePlacement="start" plain style={{ marginTop: 0 }}>导出图片</Divider>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
        <Field label="导出图片格式" tip="png=无损（推荐）；jpg=体积更小">
          <Select
            value={format}
            onChange={setFormat}
            style={{ width: 160 }}
            options={[{ value: 'png', label: 'png' }, { value: 'jpg', label: 'jpg' }]}
            data-testid="deploy-image-format"
          />
        </Field>
        <Field label="出图倍率" tip={`1080p / 2K / 4K；当前套餐最高 ${maxScale === 1 ? '1080p' : maxScale === 2 ? '2K' : '4K'}`}>
          <Radio.Group value={scale} onChange={(e) => setScale(e.target.value)}>
            <Radio.Button value={1} disabled={maxScale < 1}>1080p</Radio.Button>
            <Radio.Button value={2} disabled={maxScale < 2}>2K</Radio.Button>
            <Radio.Button value={4} disabled={maxScale < 4}>4K</Radio.Button>
          </Radio.Group>
        </Field>
      </div>
      <div style={{ fontSize: 12, color: showWatermark ? '#faad14' : '#888', margin: '4px 0 12px' }}>
        {showWatermark ? '当前套餐导出图片将自动添加水印（升级/加购去水印后自动移除）' : '当前套餐无水印'}
        {target.kind !== 'scene' ? '；大屏出图取当前编辑画布' : ''}
      </div>
      <TierGateAlert artifact={artifact} policy={policy} label="导出图片" testId="deploy-image-gate" />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" icon={<PictureOutlined />} loading={exporting} disabled={!gateOk} onClick={() => void doExport()} data-testid="deploy-image-export">
          开始导出
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// 导出视频（DW-P2-8）：3D 场景画布逐帧采样录制 MP4/WebM/GIF + 水印随套餐
// 入口=第 6 Tab；仅 3D 场景视图可用（大屏/融合用导出图片）；服务端 kind='video' 计额度
// ============================================================

function VideoTab({
  target,
  artifact,
  policy,
  ensureDeployment,
}: {
  target: { kind: 'scene' | 'dashboard' | 'fusion'; name?: string };
  artifact?: DeployArtifactPolicy;
  policy: DeployPolicyView | null;
  ensureDeployment: () => Promise<DeploymentRecord>;
}) {
  const [format, setFormat] = useState<'video' | 'gif'>('video');
  const [duration, setDuration] = useState(5);
  const [fps, setFps] = useState(30);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ blob: Blob; url: string; filename: string; format: string; durationSec: number } | null>(null);
  const wmFlag = useWatermarkFlag();
  const sceneName = useSceneStore((s) => s.currentScene?.name);
  const maxDuration = Number(artifact?.maxDurationSec) > 0 ? Number(artifact?.maxDurationSec) : 15;
  const gateOk = !!artifact?.allowed;
  const showWatermark = wmFlag !== false;
  const sceneOnly = target.kind === 'scene';
  const effectiveDuration = format === 'gif' ? Math.min(duration, GIF_MAX_DURATION_SEC, maxDuration) : Math.min(duration, maxDuration);

  const doRecord = async () => {
    if (!sceneOnly) {
      message.warning('视频录制暂仅支持 3D 场景视图（大屏/融合请使用导出图片）');
      return;
    }
    setRecording(true);
    setElapsed(0);
    setResult(null);
    try {
      const res = await recordDeployVideo({
        name: target.name || sceneName || '场景',
        format: format === 'gif' ? 'gif' : 'mp4',
        durationSec: effectiveDuration,
        fps,
        watermark: showWatermark ? { text: 'DTwince', logoUrl: '/watermark-logo.png' } : null,
        onProgress: (s) => setElapsed(s),
      });
      const url = URL.createObjectURL(res.blob);
      setResult({ blob: res.blob, url, filename: res.filename, format: res.format, durationSec: res.durationSec });
      const dep = await ensureDeployment();
      await createRelease(dep.id, {
        kind: 'video',
        options: {
          format: res.format,
          durationSec: res.durationSec,
          fps: res.fps,
          width: res.width,
          height: res.height,
          watermark: showWatermark,
        },
      });
      message.success(
        `录制完成 ${res.filename}（${(res.blob.size / 1024 / 1024).toFixed(1)} MB）${showWatermark ? '，已烧录水印' : ''}`,
      );
    } catch (e) {
      message.error((e as Error).message || '录制失败');
    } finally {
      setRecording(false);
    }
  };

  const doDownload = () => {
    if (!result) return;
    downloadBlob(result.blob, result.filename);
    message.success(`已下载 ${result.filename}`);
  };

  const doSaveRecord = async () => {
    if (!result) return;
    setSaving(true);
    try {
      const dep = await ensureDeployment();
      await createRelease(dep.id, {
        kind: 'video',
        options: { format: result.format, durationSec: result.durationSec, watermark: showWatermark },
      });
      message.success('已记录发布版本（计入视频额度）');
    } catch (e) {
      message.error(deployErrorOf(e)?.message || '记录失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="deploy-video-tab">
      <Divider titlePlacement="start" plain style={{ marginTop: 0 }}>导出视频</Divider>
      {!sceneOnly && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="视频录制暂仅支持 3D 场景视图"
          description="大屏 / 融合视图请使用「导出图片」；后续版本将支持融合视图录制。"
        />
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
        <Field label="产物格式" tip="视频=MP4（浏览器支持时）否则 WebM；GIF=动图（≤8 秒，640 宽）">
          <Radio.Group value={format} onChange={(e) => setFormat(e.target.value)} data-testid="deploy-video-format">
            <Radio.Button value="video">视频（MP4/WebM）</Radio.Button>
            <Radio.Button value="gif">GIF 动图</Radio.Button>
          </Radio.Group>
        </Field>
        <Field
          label="录制时长(秒)"
          tip={`当前套餐上限 ${maxDuration} 秒；GIF 最长 ${Math.min(GIF_MAX_DURATION_SEC, maxDuration)} 秒`}
        >
          <InputNumber
            min={1}
            max={format === 'gif' ? Math.min(GIF_MAX_DURATION_SEC, maxDuration) : maxDuration}
            precision={0}
            value={duration}
            onChange={(v) => setDuration(Number(v) || 1)}
            style={{ width: 120 }}
            data-testid="deploy-video-duration"
          />
        </Field>
        {format === 'video' && (
          <Field label="帧率" tip="逐帧采样录制：24/30fps（分辨率跟随画布，最高 1080p）">
            <Select
              value={fps}
              onChange={setFps}
              style={{ width: 120 }}
              options={VIDEO_FPS_OPTIONS.map((v) => ({ value: v, label: `${v} fps` }))}
              data-testid="deploy-video-fps"
            />
          </Field>
        )}
      </div>
      <div style={{ fontSize: 12, color: showWatermark ? '#faad14' : '#888', margin: '4px 0 12px' }}>
        {showWatermark ? '当前套餐录制的视频会自动烧录水印（升级后自动去除）' : '当前套餐无水印'}
        {sceneOnly ? '' : '；视频录制需要 3D 场景视图'}
      </div>
      <TierGateAlert artifact={artifact} policy={policy} label="导出视频" testId="deploy-video-gate" />
      {recording && (
        <div style={{ marginBottom: 12 }} data-testid="deploy-video-progress">
          <Progress
            percent={Math.min(100, Math.round((elapsed / Math.max(1, effectiveDuration)) * 100))}
            status="active"
            format={() => `录制中 ${elapsed.toFixed(1)}/${effectiveDuration}s`}
          />
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button
          type="primary"
          icon={<VideoCameraOutlined />}
          loading={recording}
          disabled={!gateOk || !sceneOnly}
          onClick={() => void doRecord()}
          data-testid="deploy-video-record"
        >
          {recording ? '录制中…' : '开始录制'}
        </Button>
      </div>
      {result && (
        <div style={{ marginTop: 16 }} data-testid="deploy-video-result">
          <Divider titlePlacement="start" plain>预览</Divider>
          {result.format === 'gif' ? (
            <img
              src={result.url}
              alt="GIF 预览"
              style={{ maxWidth: 360, borderRadius: 8, border: '1px solid #303030' }}
              data-testid="deploy-video-preview"
            />
          ) : (
            <video
              src={result.url}
              controls
              loop
              autoPlay
              muted
              style={{ maxWidth: 360, borderRadius: 8, border: '1px solid #303030' }}
              data-testid="deploy-video-preview"
            />
          )}
          <div style={{ fontSize: 12, color: '#888', margin: '8px 0' }}>
            {result.filename} · {(result.blob.size / 1024 / 1024).toFixed(1)} MB · {result.durationSec}s
          </div>
          <Space>
            <Button icon={<CloudUploadOutlined />} onClick={doDownload} data-testid="deploy-video-download">
              下载文件
            </Button>
            <Button loading={saving} onClick={() => void doSaveRecord()} data-testid="deploy-video-save-record">
              记录发布版本
            </Button>
          </Space>
        </div>
      )}
    </div>
  );
}

// ============================================================
// 导出资产包（DW-P2-9）：项目 → .dtbase 签名包（scene_template/dashboard_template/model_pack）
// 服务端 dtbase v2 构建 + 7 门禁 + ed25519 签名；下载复用 releases/:id/download；kind='asset' 计额度
// ============================================================

function AssetTab({
  target,
  artifact,
  policy,
  ensureDeployment,
}: {
  target: { kind: 'scene' | 'dashboard' | 'fusion'; name?: string };
  artifact?: DeployArtifactPolicy;
  policy: DeployPolicyView | null;
  ensureDeployment: () => Promise<DeploymentRecord>;
}) {
  const typeOptions =
    target.kind === 'dashboard'
      ? [{ value: 'dashboard_template', label: '大屏模板（页面+组件）' }]
      : [
          { value: 'scene_template', label: '场景模板（场景+模型）' },
          { value: 'model_pack', label: '模型包（GLB 模型）' },
        ];
  const [assetType, setAssetType] = useState<string>(typeOptions[0].value);
  const [exporting, setExporting] = useState(false);
  const gateOk = !!artifact?.allowed;

  const doExport = async () => {
    setExporting(true);
    try {
      const dep = await ensureDeployment();
      const r = await createRelease(dep.id, { kind: 'asset', options: { assetType } });
      const pkg = (r.release?.manifest as { package?: { fileName?: string } } | undefined)?.package;
      await downloadReleaseFile(r.release.id, pkg?.fileName ?? `${dep.projectCode}-${assetType}.dtbase`);
      message.success(`已导出资产包${pkg?.fileName ? ` ${pkg.fileName}` : ''}（dtbase v2 · ed25519 签名）`);
    } catch (e) {
      message.error(deployErrorOf(e)?.message || '导出失败');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="deploy-asset-tab">
      <Divider titlePlacement="start" plain style={{ marginTop: 0 }}>导出资产包（.dtbase）</Divider>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
        <Field
          label="资产包类型"
          tip="场景模板=场景结构与 GLB 模型；大屏模板=页面与组件；模型包=纯 GLB 模型集合"
        >
          <Radio.Group
            value={assetType}
            onChange={(e) => setAssetType(e.target.value)}
            data-testid="deploy-asset-type"
          >
            {typeOptions.map((o) => (
              <Radio.Button key={o.value} value={o.value}>{o.label}</Radio.Button>
            ))}
          </Radio.Group>
        </Field>
      </div>
      <div style={{ fontSize: 12, color: '#888', margin: '4px 0 12px' }}>
        资产包为 dtbase v2 签名包（7 项校验门禁 + ed25519 签名，含 files.sha256），用于基座沉淀 / 市场分发；
        仅收录 GLB 模型（FBX/glTF 请先转 GLB）。{target.kind === 'dashboard' ? '' : '大屏项目请切换到「导出资产包」时选择大屏目标。'}
      </div>
      <TierGateAlert artifact={artifact} policy={policy} label="导出资产包" testId="deploy-asset-gate" />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          icon={<FileZipOutlined />}
          loading={exporting}
          disabled={!gateOk}
          onClick={() => void doExport()}
          data-testid="deploy-asset-export"
        >
          导出资产包
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// 离线 U 盘交付包（DW-P2-11）：组装既有产物 + SDK + 说明 + 校验工具（面向无网现场）
// ============================================================

const USB_COMPONENTS: { key: string; label: string; desc: string }[] = [
  { key: 'installer', label: '查看器安装器', desc: 'DTwinceViewerSetup（离线安装）' },
  { key: 'package', label: '私有部署包', desc: 'viewer+快照+serve 脚本（需先导出部署文件）' },
  { key: 'edge', label: '边缘部署包', desc: 'edge-local-web 上机包（需先导出）' },
  { key: 'iot', label: '设备接入工程包', desc: '点位/协议工程（需先导出）' },
  { key: 'model', label: '边缘 AI 模型包', desc: '签名模型包（需先导出）' },
  { key: 'sdk', label: '嵌入 SDK', desc: 'sdk.js（数据对接）' },
  { key: 'rtuTool', label: 'RTU USB 配置工具', desc: '现场串口配置（可选）' },
];

function UsbTab({
  artifact,
  policy,
  ensureDeployment,
}: {
  artifact?: DeployArtifactPolicy;
  policy: DeployPolicyView | null;
  ensureDeployment: () => Promise<DeploymentRecord>;
}) {
  const [include, setInclude] = useState<Record<string, boolean>>({
    installer: true, package: true, edge: true, iot: true, model: true, sdk: true, rtuTool: false,
  });
  const [preflight, setPreflight] = useState<Record<string, UsbPreflightItem> | null>(null);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState<{ fileName: string; components: { key: string; path: string }[]; warnings: string[] } | null>(null);
  const gateOk = !!artifact?.allowed;

  // 构件预检：缺失项禁用勾选（避免"很多跳过"的困惑）
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const dep = await ensureDeployment();
        const items = await fetchUsbPreflight(dep.id);
        if (cancelled) return;
        const map: Record<string, UsbPreflightItem> = {};
        for (const it of items) map[it.key] = it;
        setPreflight(map);
        setInclude((prev) => {
          const next: Record<string, boolean> = { ...prev };
          for (const it of items) {
            if (!it.available) next[it.key] = false;
            else if (it.key !== 'rtuTool' && prev[it.key] === undefined) next[it.key] = true;
          }
          return next;
        });
      } catch {
        /* 预检失败不阻断（导出时仍会给出 warnings） */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doExport = async () => {
    setExporting(true);
    setResult(null);
    try {
      const dep = await ensureDeployment();
      const r = await createRelease(dep.id, { kind: 'usb', options: { usbInclude: include } });
      const p = (r.release?.manifest as { package?: { fileName?: string }; components?: { key: string; path: string }[]; warnings?: string[] } | undefined);
      await downloadReleaseFile(r.release.id, p?.package?.fileName ?? 'dtwince-usb-offline.zip');
      setResult({ fileName: p?.package?.fileName ?? '', components: p?.components ?? [], warnings: p?.warnings ?? [] });
      message.success(`离线交付包已导出${p?.package?.fileName ? ` ${p.package.fileName}` : ''}（含校验工具 verify/verify.mjs）`);
    } catch (e) {
      message.error(deployErrorOf(e)?.message || '组装失败');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="deploy-usb-tab">
      <Divider titlePlacement="start" plain style={{ marginTop: 0 }}>离线 U 盘交付包</Divider>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
        组装既有产物 + SDK + 说明 + 零依赖校验工具（防拷贝损坏），面向无网现场。
        {preflight ? '灰色项为当前项目尚未生成/未发布的构件（需先在其他 Tab 导出），勾选后会自动跳过并在 README 标注。' : '正在检查构件可用性…'}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', marginBottom: 12 }}>
        {USB_COMPONENTS.map((c) => {
          const pf = preflight?.[c.key];
          const unavailable = !!preflight && pf ? !pf.available : false;
          return (
            <Checkbox
              key={c.key}
              checked={!!include[c.key]}
              disabled={unavailable}
              onChange={(e) => setInclude((prev) => ({ ...prev, [c.key]: e.target.checked }))}
              data-testid={`deploy-usb-${c.key}`}
            >
              {c.label}
              <span style={{ color: '#888', fontSize: 12 }}>（{c.desc}）</span>
              {unavailable ? <Tag style={{ marginLeft: 6 }} color="default">未就绪</Tag> : null}
            </Checkbox>
          );
        })}
      </div>
      <TierGateAlert artifact={artifact} policy={policy} label="离线 U 盘交付包" testId="deploy-usb-gate" />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          icon={<UsbOutlined />}
          loading={exporting}
          disabled={!gateOk}
          onClick={() => void doExport()}
          data-testid="deploy-usb-export"
        >
          组装并下载离线包
        </Button>
      </div>
      {result && (
        <div style={{ marginTop: 12 }} data-testid="deploy-usb-result">
          <Divider titlePlacement="start" plain>组装结果</Divider>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>{result.fileName}</div>
          {result.components.map((c) => (
            <div key={c.key} style={{ fontSize: 12, lineHeight: '20px' }}>
              <Tag color="green">已包含</Tag>
              {c.path}
            </div>
          ))}
          {result.warnings.map((w) => (
            <div key={w} style={{ fontSize: 12, lineHeight: '20px' }}>
              <Tag color="orange">跳过</Tag>
              {w}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// 占位 Tab（部署包 / EXE / 边缘与对接）：视觉对齐参考图，能力下一阶段开放
// ============================================================

function PlaceholderTab({
  title,
  artifact,
  policy,
  hint,
  fields,
  advanced,
}: {
  title: string;
  artifact?: DeployArtifactPolicy;
  policy: DeployPolicyView | null;
  hint: string;
  fields: string[];
  advanced: string[];
}) {
  return (
    <div data-testid={`deploy-placeholder-${title}`}>
      <Divider titlePlacement="start" plain style={{ marginTop: 0 }}>基础设置</Divider>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px', marginBottom: 12 }}>
        {fields.map((f) => (
          <Field key={f} label={f} tip="该能力将在下一阶段开放">
            <Input disabled placeholder="下一阶段开放（P1）" />
          </Field>
        ))}
      </div>
      {advanced.length > 0 && (
        <>
          <Divider titlePlacement="start" plain>高级设置</Divider>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px', marginBottom: 12 }}>
            {advanced.map((f) => (
              <Field key={f} label={f} tip="该能力将在下一阶段开放">
                <Input disabled placeholder="下一阶段开放（P1）" />
              </Field>
            ))}
          </div>
        </>
      )}
      <TierGateAlert artifact={artifact} policy={policy} label={title} testId="deploy-placeholder-gate" />
      <Alert type="info" showIcon style={{ marginBottom: 12 }} message={hint} />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title="下一阶段开放">
          <Button type="primary" disabled data-testid="deploy-placeholder-export">开始导出</Button>
        </Tooltip>
      </div>
    </div>
  );
}

// ============================================================
// 边缘与对接（DW-P1-9 区块1：边缘部署；区块2/3 占位 P1-10/11）
// 边缘部署包（viewer+快照+edge-config）→ OTA 定向/批量/灰度下发
// ============================================================

function EdgeTab({
  target,
  artifact,
  policy,
  ensureDeployment,
}: {
  target: { kind: string; name?: string };
  artifact?: DeployArtifactPolicy;
  policy: DeployPolicyView | null;
  ensureDeployment: () => Promise<DeploymentRecord>;
}) {
  const [deviceSns, setDeviceSns] = useState('');
  const [gray, setGray] = useState(100);
  const [force, setForce] = useState(true);
  const [note, setNote] = useState('');
  const [exporting, setExporting] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const [pkg, setPkg] = useState<{ releaseId: string; fileName: string; size: number } | null>(null);
  const [deliveries, setDeliveries] = useState<DeliverRecord[]>([]);
  // DW-P1-10：设备接入工程包
  const [iotStats, setIotStats] = useState<{ bindings: number; devices: number; points: number } | null>(null);
  const [iotPkg, setIotPkg] = useState<{ releaseId: string; fileName: string; size: number } | null>(null);
  const [iotExporting, setIotExporting] = useState(false);
  const [iotReport, setIotReport] = useState<IotValidateReport | null>(null);
  const [validating, setValidating] = useState(false);
  // DW-P1-11：数据对接（开放范围 + 数据令牌）
  const [iotBundle, setIotBundle] = useState<IotPackageView | null>(null);
  const [dataInfo, setDataInfo] = useState<DataTokenInfo | null>(null);
  const [scopeDevices, setScopeDevices] = useState<string[]>([]);
  const [scopePoints, setScopePoints] = useState<string[]>([]);
  const [scopeWidgets, setScopeWidgets] = useState('');
  const [scopeOrigins, setScopeOrigins] = useState('');
  const [scopeQps, setScopeQps] = useState<number | null>(null);
  const [scopeSaving, setScopeSaving] = useState(false);
  const [dataBusy, setDataBusy] = useState(false);
  const scopeInitialized = useRef(false);

  const gateOk = !!artifact?.allowed;
  const deviceList = () =>
    deviceSns
      .split(/[\s,，;；]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  const loadDeliveries = useCallback(async (releaseId: string) => {
    try {
      setDeliveries(await listDeliveries(releaseId));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (pkg) void loadDeliveries(pkg.releaseId);
  }, [pkg, loadDeliveries]);

  // 工程包统计 + 数据对接初始化（开放范围/令牌）
  useEffect(() => {
    ensureDeployment()
      .then(async (d) => {
        const [bundle, info] = await Promise.all([getIotPackage(d.id), getDataToken(d.id)]);
        const s = (bundle.meta as { stats?: { bindings: number; devices: number; points: number } })?.stats;
        if (s) setIotStats(s);
        setIotBundle(bundle);
        setDataInfo(info);
        if (!scopeInitialized.current) {
          scopeInitialized.current = true;
          setScopeDevices(info.expose.devices ?? []);
          setScopePoints(info.expose.points ?? []);
          setScopeWidgets((info.expose.widgets ?? []).join(', '));
          setScopeOrigins((info.expose.origins ?? []).join('\n'));
          setScopeQps(typeof info.expose.qps === 'number' ? info.expose.qps : null);
        }
      })
      .catch(() => {});
  }, [ensureDeployment]);

  const saveScope = async () => {
    setScopeSaving(true);
    try {
      const dep = await ensureDeployment();
      const r = await putDataScope(dep.id, {
        widgets: scopeWidgets.split(/[,，\s]+/).filter(Boolean),
        devices: scopeDevices,
        points: scopePoints,
        origins: scopeOrigins.split(/\s+/).filter(Boolean),
        ...(scopeQps ? { qps: scopeQps } : {}),
      });
      setDataInfo((prev) => (prev ? { ...prev, expose: r.expose } : prev));
      message.success('数据对接开放范围已保存');
    } catch (e) {
      message.error(deployErrorOf(e).message);
    } finally {
      setScopeSaving(false);
    }
  };

  const genDataToken = async (rotate: boolean) => {
    setDataBusy(true);
    try {
      const dep = await ensureDeployment();
      const r = await createDataToken(dep.id, rotate);
      setDataInfo({ token: r.token, tokenId: r.tokenId, scopes: r.scopes, expose: r.expose, capabilities: r.capabilities });
      message.success(rotate ? '数据令牌已轮换（旧令牌立即失效）' : '数据令牌已生成');
    } catch (e) {
      const err = deployErrorOf(e);
      if (['DEPLOY_TIER_REQUIRED', 'DEPLOY_KIND_DISABLED', 'DEPLOY_QUOTA_EXCEEDED'].includes(err.code)) {
        Modal.warning({ title: '暂时无法生成', content: err.message, okText: '知道了' });
      } else {
        message.error(err.message);
      }
    } finally {
      setDataBusy(false);
    }
  };

  const killDataToken = async () => {
    setDataBusy(true);
    try {
      const dep = await ensureDeployment();
      await revokeDataToken(dep.id);
      setDataInfo((prev) => (prev ? { ...prev, token: null, tokenId: undefined } : prev));
      message.success('数据令牌已吊销（画面链接不受影响）');
    } catch (e) {
      message.error(deployErrorOf(e).message);
    } finally {
      setDataBusy(false);
    }
  };

  const copyText = (text: string, label: string) => {
    void navigator.clipboard
      ?.writeText(text)
      .then(() => message.success(`${label}已复制`))
      .catch(() => message.warning('复制失败，请手动选择'));
  };

  const doExportIot = async () => {
    setIotExporting(true);
    try {
      const dep = await ensureDeployment();
      const r = await exportIotPackage(dep.id);
      if (r.status !== 'ready') throw new Error(`生成未完成（${r.status}）`);
      setIotPkg({ releaseId: r.releaseId, fileName: r.package?.fileName ?? '', size: Number(r.package?.size ?? 0) });
      message.success('设备接入工程包已生成');
    } catch (e) {
      const err = deployErrorOf(e);
      if (['DEPLOY_TIER_REQUIRED', 'DEPLOY_KIND_DISABLED', 'DEPLOY_QUOTA_EXCEEDED'].includes(err.code)) {
        Modal.warning({ title: '暂时无法生成', content: err.message, okText: '知道了' });
      } else {
        message.error(err.message);
      }
    } finally {
      setIotExporting(false);
    }
  };

  const validateFile = async (file: File) => {
    setValidating(true);
    setIotReport(null);
    try {
      let payload: {
        deviceBindings: Record<string, unknown>[];
        points: Record<string, string>[];
        protocolMap?: Record<string, unknown>;
      };
      if (file.name.toLowerCase().endsWith('.zip')) {
        const JSZip = (await import('jszip')).default;
        const zip = await JSZip.loadAsync(await file.arrayBuffer());
        const dbEntry = zip.file('device-bindings.json');
        const ptsEntry = zip.file('points.csv');
        const pmEntry = zip.file('protocol-map.json');
        const db = dbEntry ? (JSON.parse(await dbEntry.async('string')) as { bindings?: unknown[] } | unknown[]) : {};
        payload = {
          deviceBindings: Array.isArray(db) ? (db as Record<string, unknown>[]) : ((db as { bindings?: Record<string, unknown>[] }).bindings ?? []),
          points: ptsEntry ? parseIotPointsCsv(await ptsEntry.async('string')) : [],
          protocolMap: pmEntry ? (JSON.parse(await pmEntry.async('string')) as Record<string, unknown>) : undefined,
        };
      } else {
        const json = JSON.parse(await file.text()) as Record<string, unknown>;
        payload = {
          deviceBindings: (json.bindings ?? json.deviceBindings ?? []) as Record<string, unknown>[],
          points: (json.points ?? []) as Record<string, string>[],
          protocolMap: json.protocolMap as Record<string, unknown> | undefined,
        };
      }
      const r = await validateIotPackage(payload);
      setIotReport(r);
      message[r.ok ? 'success' : 'warning'](r.ok ? '校验通过' : `校验发现问题 ${r.errors.length} 项`);
    } catch (e) {
      message.error(`解析失败：${(e as Error).message}`);
    } finally {
      setValidating(false);
    }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const dep = await ensureDeployment();
      const res = await createRelease(dep.id, { kind: 'edge', options: { staticize: true } });
      if (res.release.status !== 'ready') throw new Error(`构建未完成（${res.release.status}）`);
      const m = res.release.manifest?.package as { fileName?: string; size?: number } | undefined;
      setPkg({ releaseId: res.release.id, fileName: String(m?.fileName ?? ''), size: Number(m?.size ?? 0) });
      message.success('边缘包已生成（viewer+快照+edge-config），可下载或直接下发');
    } catch (e) {
      const err = deployErrorOf(e);
      if (['DEPLOY_TIER_REQUIRED', 'DEPLOY_KIND_DISABLED', 'DEPLOY_QUOTA_EXCEEDED'].includes(err.code)) {
        Modal.warning({ title: '暂时无法导出', content: err.message, okText: '知道了' });
      } else {
        message.error(err.message);
      }
    } finally {
      setExporting(false);
    }
  };

  const doDeliver = async () => {
    if (!pkg) {
      message.warning('请先生成边缘包');
      return;
    }
    const devices = deviceList();
    if (devices.length === 0) {
      message.warning('请填写边缘设备序列号（逗号/换行分隔）');
      return;
    }
    setDelivering(true);
    try {
      const r = await deliverRelease(pkg.releaseId, { deviceSns: devices, gray, force, note: note.trim() || undefined });
      message.success(`已下发 ${r.devices.length} 台设备（灰度 ${gray}%${force ? '，强制' : ''}）`);
      void loadDeliveries(pkg.releaseId);
    } catch (e) {
      const err = deployErrorOf(e);
      if (['DEPLOY_EDGE_DEVICE_LIMIT', 'DEPLOY_TIER_REQUIRED', 'DEPLOY_QUOTA_EXCEEDED'].includes(err.code)) {
        Modal.warning({ title: '暂时无法下发', content: err.message, okText: '知道了' });
      } else {
        message.error(err.message);
      }
    } finally {
      setDelivering(false);
    }
  };

  return (
    <div data-testid="deploy-edge-tab">
      <Divider titlePlacement="start" plain style={{ marginTop: 0 }}>边缘部署</Divider>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
        <Field label="设备序列号" tip="支持多个，逗号/换行分隔（如 EDGE-0001）" span2>
          <Input.TextArea
            rows={2}
            value={deviceSns}
            onChange={(e) => setDeviceSns(e.target.value)}
            placeholder={'EDGE-0001\nEDGE-0002'}
            data-testid="deploy-edge-devices"
          />
        </Field>
        <Field label="灰度比例（%）" tip="设备按序列号哈希分桶，命中才升级（100=全量）">
          <InputNumber min={0} max={100} value={gray} onChange={(v) => setGray(Number(v ?? 100))} style={{ width: 140 }} data-testid="deploy-edge-gray" />
        </Field>
        <Field label="强制更新" tip="设备端跳过版本比较直接更新（现场应急）">
          <Switch checked={force} onChange={setForce} checkedChildren="强制" unCheckedChildren="普通" data-testid="deploy-edge-force" />
        </Field>
        <Field label="备注" tip="下发说明（记录在 OTA 记录，便于追溯）">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="如：展厅盒子首版下发" />
        </Field>
      </div>

      {pkg && (
        <Alert
          type="success"
          showIcon
          style={{ margin: '12px 0' }}
          data-testid="deploy-edge-result"
          message={`边缘包已生成：${pkg.fileName}（${(pkg.size / 1024 / 1024).toFixed(1)} MB）`}
          description={
            <Space>
              <Button
                size="small"
                onClick={() => void downloadReleaseFile(pkg.releaseId, pkg.fileName).catch(() => message.error('下载失败'))}
                data-testid="deploy-edge-download"
              >
                下载边缘包
              </Button>
              <Text type="secondary" style={{ fontSize: 12 }}>盒子端 OTA 校验 sha512 后原子切换（断网自治渲染快照）</Text>
            </Space>
          }
        />
      )}

      {deliveries.length > 0 && (
        <div data-testid="deploy-edge-deliveries" style={{ marginBottom: 12 }}>
          <Table<DeliverRecord>
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={deliveries}
            columns={[
              { title: '时间', dataIndex: 'createdAt', width: 170, render: (v?: string) => (v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '—') },
              { title: '设备', dataIndex: 'deviceSns', render: (v?: string[]) => (v ?? []).join(', ') },
              { title: '灰度', dataIndex: 'gray', width: 70, render: (v?: number) => `${v ?? 100}%` },
              { title: '强制', dataIndex: 'force', width: 70, render: (v?: boolean) => (v ? '是' : '否') },
            ]}
          />
        </div>
      )}

      <TierGateAlert artifact={artifact} policy={policy} label="边缘部署包" testId="deploy-edge-gate" />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button icon={<FolderOutlined />} loading={exporting} disabled={!gateOk} onClick={() => void doExport()} data-testid="deploy-edge-export">
          导出边缘包
        </Button>
        <Button type="primary" icon={<ApiOutlined />} loading={delivering} disabled={!gateOk || !pkg} onClick={() => void doDeliver()} data-testid="deploy-edge-deliver">
          下发到设备
        </Button>
      </div>

      <Divider titlePlacement="start" plain>设备接入工程包</Divider>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }} data-testid="deploy-edge-iot-stats">
        {iotStats
          ? `当前场景设备绑定：${iotStats.bindings} 条 · 设备 ${iotStats.devices} 台 · 点位 ${iotStats.points} 个`
          : '正在统计设备绑定…'}
      </div>
      {iotPkg && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 12 }}
          data-testid="deploy-edge-iot-result"
          message={`工程包已生成：${iotPkg.fileName}（${(iotPkg.size / 1024).toFixed(0)} KB）`}
          description={
            <Space>
              <Button
                size="small"
                onClick={() => void downloadReleaseFile(iotPkg.releaseId, iotPkg.fileName).catch(() => message.error('下载失败'))}
                data-testid="deploy-edge-iot-download"
              >
                下载工程包
              </Button>
              <Text type="secondary" style={{ fontSize: 12 }}>device-bindings.json / points.csv / protocol-map.json（随边缘包内嵌 iot/）</Text>
            </Space>
          }
        />
      )}
      {iotReport && (
        <Alert
          type={iotReport.ok ? 'success' : 'error'}
          showIcon
          style={{ marginBottom: 12 }}
          data-testid="deploy-edge-iot-report"
          message={
            iotReport.ok
              ? `校验通过（绑定 ${iotReport.stats.bindings} · 点位 ${iotReport.stats.points} · 设备 ${iotReport.stats.devices}）`
              : `校验未通过（${iotReport.errors.length} 项错误，${iotReport.warnings.length} 项警告）`
          }
          description={
            <div style={{ maxHeight: 150, overflow: 'auto', fontSize: 12 }}>
              {iotReport.errors.map((e, i) => (
                <div key={`e${i}`}>✗ [{e.code}] {e.message}</div>
              ))}
              {iotReport.warnings.map((w, i) => (
                <div key={`w${i}`} style={{ color: '#d48806' }}>⚠ [{w.code}] {w.message}</div>
              ))}
            </div>
          }
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 4 }}>
        <Button icon={<FolderOutlined />} loading={iotExporting} disabled={!gateOk} onClick={() => void doExportIot()} data-testid="deploy-edge-iot-export">
          生成工程包
        </Button>
        <Upload
          accept=".json,.zip"
          showUploadList={false}
          beforeUpload={(file) => {
            void validateFile(file as unknown as File);
            return false;
          }}
        >
          <Button loading={validating} data-testid="deploy-edge-iot-validate">导入校验</Button>
        </Upload>
      </div>

      <Divider titlePlacement="start" plain>数据对接</Divider>
      {(() => {
        const caps = dataInfo?.capabilities ?? { rest: false, ws: false, sdk: false };
        const origin = window.location.origin;
        const wsUrl = `${origin.replace(/^http/, 'ws')}/ws/deploy-data`;
        const sampleKey = scopePoints[0] ?? 'AUDIT9000000001::io.ai0';
        const restUrl = dataInfo?.token
          ? `${origin}/api/v1/public/data/${dataInfo.token}/query?keys=${encodeURIComponent(sampleKey)}`
          : '';
        const sdkSnippet = dataInfo?.token
          ? `<script src="${origin}/api/v1/public/data/sdk.js"></script>\n<script>\n  DTwinceEmbed.init({ viewerUrl: '${origin}/s/<发布令牌>', dataToken: '${dataInfo.token}', container: '#app' })\n    .on('ready', function () { this.query(['${sampleKey}']).then(console.log); });\n</script>`
          : '';
        const deviceOptions = Array.from(new Set((iotBundle?.points ?? []).map((p) => p.imei).filter(Boolean)));
        const pointOptions = (iotBundle?.points ?? []).map((p) => `${p.imei}::${p.pointKey}`);
        return (
          <>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }} data-testid="deploy-data-caps">
              能力：只读 REST {caps.rest ? '✓' : '—'} · WebSocket {caps.ws ? '✓' : '（专业版+）'} · 嵌入 SDK{' '}
              {caps.sdk ? '✓' : '（尊享版）'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
              <Field label="开放设备" tip="勾选允许第三方读取的设备（IMEI）；点位需同时勾选">
                <Select
                  mode="multiple"
                  value={scopeDevices}
                  onChange={(v) => setScopeDevices(v as string[])}
                  options={deviceOptions.map((d) => ({ value: d, label: d }))}
                  placeholder="从设备绑定中选择"
                  style={{ width: '100%' }}
                  data-testid="deploy-data-devices"
                />
              </Field>
              <Field label="开放点位" tip="勾选允许读取的点位（imei::pointKey）；仅白名单内可用">
                <Select
                  mode="multiple"
                  value={scopePoints}
                  onChange={(v) => setScopePoints(v as string[])}
                  options={pointOptions.map((p) => ({ value: p, label: p }))}
                  placeholder="从点位表中选择"
                  style={{ width: '100%' }}
                  showSearch
                  data-testid="deploy-data-points"
                />
              </Field>
              <Field label="开放组件（widget id，逗号分隔）" tip="大屏组件 id（快照冻结值，键为 widget:{id}）">
                <Input value={scopeWidgets} onChange={(e) => setScopeWidgets(e.target.value)} placeholder="comp-xxx, comp-yyy" data-testid="deploy-data-widgets" />
              </Field>
              <Field label="来源域名白名单" tip="每行一个（含协议，如 https://demo.com）；* 或留空=不限制（服务端 CORS 校验）">
                <Input.TextArea
                  rows={2}
                  value={scopeOrigins}
                  onChange={(e) => setScopeOrigins(e.target.value)}
                  placeholder={'https://demo.com\n*'}
                  data-testid="deploy-data-origins"
                />
              </Field>
              <Field label="限频 QPS" tip="每秒请求上限（1-100）；留空=套餐默认（策略 maxQps）">
                <InputNumber
                  min={1}
                  max={100}
                  value={scopeQps ?? undefined}
                  onChange={(v) => setScopeQps(v === null || v === undefined ? null : Number(v))}
                  style={{ width: 140 }}
                  data-testid="deploy-data-qps"
                />
              </Field>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <Button loading={scopeSaving} disabled={!gateOk} onClick={() => void saveScope()} data-testid="deploy-data-scope-save">
                保存开放范围
              </Button>
            </div>

            {dataInfo?.token ? (
              <Alert
                type="success"
                showIcon
                style={{ marginBottom: 12 }}
                data-testid="deploy-data-token-result"
                message="数据令牌已就绪（独立 scope，可单独轮换/吊销）"
                description={
                  <div style={{ fontSize: 12, lineHeight: '22px' }}>
                    <div>
                      REST：<Text copyable={{ text: restUrl }} style={{ color: '#7ecbff', wordBreak: 'break-all' }}>{restUrl}</Text>
                    </div>
                    <div>
                      WS：<Text copyable={{ text: `${wsUrl}（auth.token=${dataInfo.token}）` }} style={{ wordBreak: 'break-all' }}>
                        {wsUrl}
                      </Text>
                      <Text type="secondary">（auth.token={"<数据令牌>"}）</Text>
                    </div>
                    <div style={{ marginTop: 4 }}>
                      SDK：
                      <Button size="small" onClick={() => copyText(sdkSnippet, '嵌入示例')} data-testid="deploy-data-sdk">
                        复制嵌入示例
                      </Button>
                      {!caps.sdk ? <Tag style={{ marginLeft: 6 }}>尊享版</Tag> : null}
                    </div>
                  </div>
                }
              />
            ) : (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                data-testid="deploy-data-token-empty"
                message="尚未生成数据令牌（生成后第三方可按上方开放范围只读拉取数据）"
              />
            )}

            <TierGateAlert artifact={artifact} policy={policy} label="数据对接" testId="deploy-data-gate" />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              {!dataInfo?.token ? (
                <Button type="primary" loading={dataBusy} disabled={!gateOk} onClick={() => void genDataToken(false)} data-testid="deploy-data-token-create">
                  生成数据令牌
                </Button>
              ) : (
                <>
                  <Button loading={dataBusy} onClick={() => void genDataToken(true)} data-testid="deploy-data-token-rotate">
                    轮换令牌
                  </Button>
                  <Button danger loading={dataBusy} onClick={() => void killDataToken()} data-testid="deploy-data-token-revoke">
                    吊销令牌
                  </Button>
                </>
              )}
            </div>
          </>
        );
      })()}
      <ModelZone policy={policy} ensureDeployment={ensureDeployment} />
    </div>
  );
}

// ============================================================
// 区块4（DW-P2-10）：边缘 AI 模型包——模型上传/签名打包/下载/相机下发（M6 通道）
// ============================================================

function ModelZone({
  policy,
  ensureDeployment,
}: {
  policy: DeployPolicyView | null;
  ensureDeployment: () => Promise<DeploymentRecord>;
}) {
  const artifact = policy?.artifacts?.model;
  const gateOk = !!artifact?.allowed;
  const [models, setModels] = useState<{ name: string; size: number; format: string }[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [labels, setLabels] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pkg, setPkg] = useState<{ releaseId: string; fileName: string } | null>(null);
  const [deviceSns, setDeviceSns] = useState('');
  const [delivering, setDelivering] = useState(false);
  const [deliverResult, setDeliverResult] = useState<{
    total: number;
    queued: number;
    results: { imei: string; ok: boolean; error?: string }[];
  } | null>(null);

  const loadModels = useCallback(async () => {
    try {
      const items = await listModelFiles();
      setModels(items);
      if (items.length > 0 && !selected) setSelected(items[0].name);
    } catch {
      /* 静默：未上传过模型时为空 */
    }
  }, [selected]);

  useEffect(() => {
    void loadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doUpload = async (file: File) => {
    setUploading(true);
    try {
      const r = await uploadModelFile(file);
      message.success(`模型已上传：${r.modelName}（${(r.size / 1024).toFixed(0)} KB，sha256 ${r.sha256.slice(0, 12)}…）`);
      await loadModels();
      setSelected(r.modelName);
    } catch (e) {
      message.error(deployErrorOf(e)?.message || '上传失败（仅支持 .rknn/.onnx）');
    } finally {
      setUploading(false);
    }
    return false;
  };

  const doExport = async () => {
    if (!selected) {
      message.warning('请先选择/上传模型文件');
      return;
    }
    setBusy(true);
    try {
      const dep = await ensureDeployment();
      const r = await createRelease(dep.id, { kind: 'model', options: { modelName: selected, labels } });
      const p = (r.release?.manifest as { package?: { fileName?: string } } | undefined)?.package;
      await downloadReleaseFile(r.release.id, p?.fileName ?? 'model-pack.zip');
      setPkg({ releaseId: r.release.id, fileName: p?.fileName ?? 'model-pack.zip' });
      message.success(`模型包已导出${p?.fileName ? ` ${p.fileName}` : ''}（ed25519 签名）`);
    } catch (e) {
      message.error(deployErrorOf(e)?.message || '导出失败');
    } finally {
      setBusy(false);
    }
  };

  const doDeliver = async () => {
    if (!pkg) {
      message.warning('请先导出模型包');
      return;
    }
    const sns = Array.from(
      new Set(
        deviceSns
          .split(/[\n,，;；\s]+/)
          .map((s) => s.trim())
          .filter(Boolean),
      ),
    );
    if (sns.length === 0) {
      message.warning('请填写设备 IMEI（逗号/换行分隔）');
      return;
    }
    setDelivering(true);
    setDeliverResult(null);
    try {
      const r = await deliverModelPackage(pkg.releaseId, sns);
      setDeliverResult(r);
      message.success(`下发完成：成功 ${r.queued}/${r.total}`);
    } catch (e) {
      message.error(deployErrorOf(e)?.message || '下发失败');
    } finally {
      setDelivering(false);
    }
  };

  return (
    <div data-testid="deploy-model-zone" style={{ marginTop: 8 }}>
      <Divider titlePlacement="start" plain>边缘 AI 模型包（M6：签名包 + 相机下发）</Divider>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
        <Field label="模型文件" tip="支持 .rknn（RK 相机）/ .onnx（Jetson）；导出为签名 ZIP（manifest+labels+signature）">
          <Space.Compact style={{ width: '100%' }}>
            <Select
              value={selected || undefined}
              onChange={setSelected}
              placeholder={models.length ? '选择已上传模型' : '暂无模型，请先上传'}
              options={models.map((m) => ({ value: m.name, label: `${m.name}（${m.format} · ${(m.size / 1024).toFixed(0)} KB）` }))}
              style={{ width: 'calc(100% - 110px)' }}
              data-testid="deploy-model-select"
            />
            <Upload
              maxCount={1}
              showUploadList={false}
              beforeUpload={(f) => void doUpload(f as unknown as File)}
              accept=".rknn,.onnx"
            >
              <Button icon={<CloudUploadOutlined />} loading={uploading} data-testid="deploy-model-upload">上传</Button>
            </Upload>
          </Space.Compact>
        </Field>
        <Field label="类别标签（可选）" tip="换行/逗号分隔，写入 labels.txt；示例：person / car / bus">
          <Input.TextArea rows={2} value={labels} onChange={(e) => setLabels(e.target.value)} data-testid="deploy-model-labels" />
        </Field>
      </div>
      <TierGateAlert artifact={artifact} policy={policy} label="边缘 AI 模型包" testId="deploy-model-gate" />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Button
          type="primary"
          icon={<FileZipOutlined />}
          loading={busy}
          disabled={!gateOk || !selected}
          onClick={() => void doExport()}
          data-testid="deploy-model-export"
        >
          导出模型包
        </Button>
      </div>

      {pkg && (
        <div style={{ borderTop: '1px dashed #303030', paddingTop: 8 }} data-testid="deploy-model-result">
          <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
            已生成：{pkg.fileName}（授权 feature：model:…，随边缘 license 体系）— 可直接下发到相机
          </div>
          <Field label="目标设备 IMEI" tip="逗号/换行分隔；下发走相机 HTTP 通道（POST /api/models/upload），仅 .rknn 生效">
            <Input.TextArea
              rows={2}
              value={deviceSns}
              onChange={(e) => setDeviceSns(e.target.value)}
              placeholder="如 AUDIT9000000001, ATK1126B0000001"
              data-testid="deploy-model-sns"
            />
          </Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <Button loading={delivering} onClick={() => void doDeliver()} data-testid="deploy-model-deliver">
              下发到设备
            </Button>
          </div>
          {deliverResult && (
            <div data-testid="deploy-model-deliver-result">
              {deliverResult.results.map((r) => (
                <div key={r.imei} style={{ fontSize: 12, lineHeight: '20px' }}>
                  <Tag color={r.ok ? 'green' : 'red'}>{r.ok ? '成功' : '失败'}</Tag>
                  {r.imei}
                  {r.error ? <Text type="secondary">（{r.error}）</Text> : null}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// 导出部署文件（DW-P1-5）：私有部署包（Viewer+快照+资产+离线激活）
// ============================================================

function PackageTab({
  target,
  artifact,
  policy,
  ensureDeployment,
}: {
  target: { kind: string; name?: string };
  artifact?: DeployArtifactPolicy;
  policy: DeployPolicyView | null;
  ensureDeployment: () => Promise<DeploymentRecord>;
}) {
  const [appName, setAppName] = useState(target.name ? `${target.name}-数字孪生系统` : '数字孪生系统');
  const [projectCode, setProjectCode] = useState('');
  const [deployMode, setDeployMode] = useState<'final' | 'trial' | 'trial_extended'>('final');
  const [staticize, setStaticize] = useState(true);
  const [openEdit, setOpenEdit] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pkg, setPkg] = useState<{ releaseId: string; fileName: string; size: number } | null>(null);

  const gateOk = !!artifact?.allowed;
  const isEnterprise = policy?.tier === 'enterprise';

  useEffect(() => {
    ensureDeployment()
      .then((d) => setProjectCode(d.projectCode))
      .catch(() => {});
  }, [ensureDeployment]);

  const doExport = async () => {
    setExporting(true);
    try {
      const dep = await ensureDeployment();
      await updateDeployment(dep.id, { name: appName });
      const res = await createRelease(dep.id, {
        kind: 'package',
        options: { staticize, deployMode, openEdit },
      });
      if (res.release.status !== 'ready') throw new Error(`构建未完成（${res.release.status}）`);
      const m = res.release.manifest?.package as { fileName?: string; size?: number } | undefined;
      setPkg({ releaseId: res.release.id, fileName: String(m?.fileName ?? ''), size: Number(m?.size ?? 0) });
      message.success('部署包已生成，可下载后按说明部署（serve.mjs / nginx）');
    } catch (e) {
      const err = deployErrorOf(e);
      if (['DEPLOY_TIER_REQUIRED', 'DEPLOY_KIND_DISABLED', 'DEPLOY_QUOTA_EXCEEDED'].includes(err.code)) {
        Modal.warning({ title: '暂时无法导出', content: err.message, okText: '知道了' });
      } else {
        message.error(err.message);
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="deploy-package-tab">
      <Divider titlePlacement="start" plain style={{ marginTop: 0 }}>基础设置</Divider>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
        <Field label="项目授权 · 项目码" tip="部署包激活凭据（随授权签发）；包内 activate.html/项目码用于查看器绑定">
          <Input value={projectCode} readOnly placeholder="生成中…" data-testid="deploy-pkg-code" />
        </Field>
        <Field label="有效期" tip="最终部署=按授权有效期；临时部署默认 30 天（由授权到期时间控制，过期自动降级）">
          <Input
            readOnly
            value={
              deployMode === 'final'
                ? '按授权（长期/永久）'
                : deployMode === 'trial'
                  ? '临时 30 天（可延期）'
                  : '临时延期（顺延）'
            }
          />
        </Field>
        <Field label="部署方式" tip="最终部署=正式交付；临时部署=试用（水印+到期拦截）；临时部署延期=续期">
          <Radio.Group value={deployMode} onChange={(e) => setDeployMode(e.target.value)} data-testid="deploy-pkg-mode">
            <Radio value="final">最终部署</Radio>
            <Radio value="trial">临时部署</Radio>
            <Radio value="trial_extended">临时部署延期</Radio>
          </Radio.Group>
        </Field>
        <Field label="应用名称" tip="部署包内展示名称；示例：现代物流模型-数字孪生系统">
          <Input value={appName} onChange={(e) => setAppName(e.target.value)} data-testid="deploy-pkg-app-name" />
        </Field>
        <Field label="动态数据源转静态（数据库 / API）" tip="导出时冻结数据（离线可用，推荐开启）">
          <Switch checked={staticize} onChange={setStaticize} checkedChildren="是" unCheckedChildren="否" data-testid="deploy-pkg-staticize" />
        </Field>
      </div>
      <Divider titlePlacement="start" plain>项目 OEM</Divider>
      <OemEditor ensureDeployment={ensureDeployment} />

      <Divider titlePlacement="start" plain>高级设置</Divider>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
        <Field label="查看器发布设置" tip="基础/高级功能集（漫游/第一人称/事件联动）随授权档位（后续开放）">
          <Select disabled value="basic" style={{ width: '100%' }} options={[{ value: 'basic', label: '基础功能' }]} />
        </Field>
        <Field label="高级数据源" tip="SQL/MQTT/数据集代理直连随授权档位（后续开放）">
          <Select disabled value="basic" style={{ width: '100%' }} options={[{ value: 'basic', label: '基础功能' }]} />
        </Field>
        <Field label="APP 控制 / 云渲染" tip="规划中（敬请期待）">
          <Space>
            <Switch checked={false} disabled /> <Text type="secondary" style={{ fontSize: 12 }}>规划中</Text>
          </Space>
        </Field>
        <Field
          label="是否开放编辑修改权限"
          tip={isEnterprise ? '尊享版专属：包内附带编辑器（完整私有化在下一阶段提供）' : '尊享版专属能力，当前套餐不可用'}
        >
          <Space>
            <Switch
              checked={openEdit}
              onChange={setOpenEdit}
              disabled={!isEnterprise}
              data-testid="deploy-pkg-open-edit"
            />
            {!isEnterprise ? <Tag>尊享版</Tag> : null}
          </Space>
        </Field>
      </div>

      {pkg && (
        <Alert
          type="success"
          showIcon
          style={{ margin: '12px 0' }}
          data-testid="deploy-pkg-result"
          message={`部署包已生成：${pkg.fileName}（${(pkg.size / 1024 / 1024).toFixed(1)} MB）`}
          description={
            <Space>
              <Button
                size="small"
                onClick={() => void downloadReleaseFile(pkg.releaseId, pkg.fileName).catch(() => message.error('下载失败'))}
                data-testid="deploy-pkg-download"
              >
                下载部署包
              </Button>
              <Text type="secondary" style={{ fontSize: 12 }}>解压后 `node serve/serve.mjs` 即可本地运行</Text>
            </Space>
          }
        />
      )}
      <TierGateAlert artifact={artifact} policy={policy} label="导出部署文件" testId="deploy-pkg-gate" />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          icon={<FolderOutlined />}
          loading={exporting}
          disabled={!gateOk}
          onClick={() => void doExport()}
          data-testid="deploy-pkg-export"
        >
          开始导出
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// 导出 EXE 文件（DW-P1-3/4）：基础设置 / 购买激活码 / 购买记录
// 项目包 .dtwpkg（机器码绑定）→ 查看器导入激活（离线可用）
// ============================================================

function ExeTab({
  target,
  artifact,
  policy,
  ensureDeployment,
}: {
  target: { kind: string; name?: string };
  artifact?: DeployArtifactPolicy;
  policy: DeployPolicyView | null;
  ensureDeployment: () => Promise<DeploymentRecord>;
}) {
  const openPay = usePayIntentStore((s) => s.openPay);
  const [subTab, setSubTab] = useState('basic');
  // 基础设置
  const [appName, setAppName] = useState(target.name ? `${target.name}-数字孪生系统` : '数字孪生系统');
  const [staticize, setStaticize] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [pkg, setPkg] = useState<{ releaseId: string; fileName: string; size: number } | null>(null);
  // 购买激活码
  const [machineCode, setMachineCode] = useState('');
  const [tiers, setTiers] = useState<PayLicenseTier[]>([]);
  const [tierCode, setTierCode] = useState('');
  const [activationCode, setActivationCode] = useState('');
  const [buying, setBuying] = useState(false);
  // 购买记录
  const [records, setRecords] = useState<DeployActivationRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);

  const gateOk = !!artifact?.allowed;
  // 查看器安装器（.exe）：EXE 交付 = 安装器 + 项目包（.dtwpkg）
  const [installer, setInstaller] = useState<{ version: string; fileUrl: string; size: number } | null>(null);
  useEffect(() => {
    fetchViewerInstaller()
      .then(setInstaller)
      .catch(() => setInstaller(null));
  }, []);

  useEffect(() => {
    if (subTab === 'buy' && tiers.length === 0) {
      fetchLicenseTiers()
        .then((r) => {
          setTiers(r.items);
          if (r.items[0]) setTierCode((prev) => prev || r.items[0].code);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab]);

  const loadRecords = useCallback(async () => {
    setRecordsLoading(true);
    try {
      const dep = await ensureDeployment();
      setRecords(await listActivations(dep.id));
    } catch {
      /* 忽略（错误由 ensureDeployment 抛出时也已提示） */
    } finally {
      setRecordsLoading(false);
    }
  }, [ensureDeployment]);

  useEffect(() => {
    if (subTab === 'records') void loadRecords();
  }, [subTab, loadRecords]);

  const doExport = async () => {
    setExporting(true);
    try {
      const dep = await ensureDeployment();
      await updateDeployment(dep.id, { name: appName });
      const res = await createRelease(dep.id, { kind: 'exe', options: { staticize } });
      if (res.release.status !== 'ready') throw new Error(`构建未完成（${res.release.status}）`);
      const m = res.release.manifest?.package as { fileName?: string; size?: number } | undefined;
      setPkg({ releaseId: res.release.id, fileName: String(m?.fileName ?? ''), size: Number(m?.size ?? 0) });
      message.success('项目包已生成，可下载后由查看器导入激活');
    } catch (e) {
      const err = deployErrorOf(e);
      if (['DEPLOY_TIER_REQUIRED', 'DEPLOY_KIND_DISABLED', 'DEPLOY_QUOTA_EXCEEDED'].includes(err.code)) {
        Modal.warning({ title: '暂时无法导出', content: err.message, okText: '知道了' });
      } else {
        message.error(err.message);
      }
    } finally {
      setExporting(false);
    }
  };

  const afterPaid = async (depId: string) => {
    try {
      const r = await issueActivation(depId, { machineCode: machineCode.trim() });
      setActivationCode(r.activationCode);
      try {
        await navigator.clipboard.writeText(r.activationCode);
        message.success('购买成功，激活码已复制到剪贴板');
      } catch {
        message.success('购买成功，请手动复制激活码');
      }
      void loadRecords();
    } catch (e) {
      message.error(deployErrorOf(e).message);
    }
  };

  const doBuy = async () => {
    if (!machineCode.trim()) {
      message.warning('请先填写机器码（查看器激活页展示）');
      return;
    }
    const tier = tiers.find((t) => t.code === tierCode);
    if (!tier) {
      message.warning('请选择授权档位');
      return;
    }
    setBuying(true);
    try {
      const dep = await ensureDeployment();
      openPay(
        {
          orderType: 'license',
          productRef: tier.code,
          amount: Number(tier.price),
          deviceSn: machineCode.trim(),
          projectCode: dep.projectCode,
          subject: `部署项目授权-${tier.name}（${dep.projectCode}）`,
        },
        () => {
          void afterPaid(dep.id);
        },
      );
    } finally {
      setBuying(false);
    }
  };

  const revoke = async (id: string) => {
    try {
      await revokeActivation(id);
      message.success('已吊销');
      void loadRecords();
    } catch (e) {
      message.error(deployErrorOf(e).message);
    }
  };

  const maskCode = (code?: string | null) => (code ? `${code.slice(0, 10)}…` : '—');
  const currentTier = tiers.find((t) => t.code === tierCode);

  return (
    <div data-testid="deploy-exe-tab">
      <Tabs
        activeKey={subTab}
        onChange={setSubTab}
        items={[
          {
            key: 'basic',
            label: '基础设置',
            children: (
              <div>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  data-testid="deploy-exe-guide"
                  message="EXE 交付由两部分组成：查看器安装器（.exe）+ 项目包（.dtwpkg）"
                  description={
                    <div style={{ fontSize: 12, lineHeight: '20px' }}>
                      1. 先下载并安装 <b>DTwinceViewer</b> 查看器（Windows .exe，机器装一次即可）；
                      2. 在本页导出 <b>.dtwpkg 项目包</b>（每个项目一份，不直接双击安装）；
                      3. 打开查看器，选择导入/打开项目包（或把 .dtwpkg 拖入窗口）；
                      4. 复制查看器机器码 →「购买激活码」购买并粘贴激活（离线可用）。
                      <div style={{ marginTop: 6 }}>
                        <Button
                          size="small"
                          type="primary"
                          ghost
                          icon={<DownloadOutlined />}
                          disabled={!installer}
                          onClick={() => installer && window.open(installer.fileUrl, '_blank')}
                          data-testid="deploy-exe-installer"
                        >
                          {installer ? `下载查看器安装器 v${installer.version}（${(installer.size / 1024 / 1024).toFixed(0)} MB）` : '查看器安装器未发布（联系管理员）'}
                        </Button>
                      </div>
                    </div>
                  }
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
                  <Field label="应用名称" tip="查看器窗口标题/项目名；示例：现代物流模型-数字孪生系统">
                    <Input value={appName} onChange={(e) => setAppName(e.target.value)} data-testid="deploy-exe-app-name" />
                  </Field>
                  <Field label="动态数据源转静态" tip="导出时把数据库/API 数据冻结进项目包（离线可用，推荐开启）">
                    <Switch checked={staticize} onChange={setStaticize} checkedChildren="是" unCheckedChildren="否" data-testid="deploy-exe-staticize" />
                  </Field>
                </div>
                <Divider titlePlacement="start" plain>项目 OEM</Divider>
                <OemEditor ensureDeployment={ensureDeployment} />
                <Alert
                  type="info"
                  showIcon
                  style={{ margin: '12px 0' }}
                  message="导出后使用四步"
                  description={
                    <div style={{ fontSize: 12, lineHeight: '20px' }}>
                      1. 导出并下载项目包（.dtwpkg）；2. 安装 DTwince Viewer 后导入项目包；
                      3. 复制查看器展示的机器码；4. 在「购买激活码」购买并粘贴激活码完成激活（离线可用）。
                    </div>
                  }
                />
                {pkg && (
                  <Alert
                    type="success"
                    showIcon
                    style={{ marginBottom: 12 }}
                    data-testid="deploy-exe-result"
                    message={`项目包已生成：${pkg.fileName}`}
                    description={
                      <Space>
                        <Button
                          size="small"
                          onClick={() => void downloadReleaseFile(pkg.releaseId, pkg.fileName).catch(() => message.error('下载失败'))}
                          data-testid="deploy-exe-download"
                        >
                          下载项目包
                        </Button>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          （.dtwpkg 由 DTwinceViewer 查看器导入，不是安装程序；安装器见上方按钮）
                        </Text>
                      </Space>
                    }
                  />
                )}
                <TierGateAlert artifact={artifact} policy={policy} label="导出 EXE 文件" testId="deploy-exe-gate" />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="primary"
                    icon={<WindowsOutlined />}
                    loading={exporting}
                    disabled={!gateOk}
                    onClick={() => void doExport()}
                    data-testid="deploy-exe-export"
                  >
                    导出EXE文件
                  </Button>
                </div>
              </div>
            ),
          },
          {
            key: 'buy',
            label: '购买激活码',
            children: (
              <div>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="购买后自动签发激活码（绑定机器码 + 项目码，离线校验）"
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
                  <Field label="机器码" tip="查看器激活页「本机机器码」复制（形如 DTWIN-XXXXXXXXXXXX）">
                    <Input
                      value={machineCode}
                      onChange={(e) => setMachineCode(e.target.value)}
                      placeholder="DTWIN-XXXXXXXXXXXX"
                      data-testid="deploy-exe-machine"
                    />
                  </Field>
                  <Field label="授权档位（到期时间）" tip="以平台设备授权档位计价；到期后可续费顺延">
                    <Select
                      value={tierCode || undefined}
                      onChange={setTierCode}
                      style={{ width: '100%' }}
                      placeholder="选择档位"
                      data-testid="deploy-exe-tier"
                      options={tiers.map((t) => ({
                        value: t.code,
                        label: `${t.name} · ¥${t.price} / ${t.unitLabel}（${t.durationDays >= 3650 ? '长期' : `${t.durationDays} 天`}）`,
                      }))}
                    />
                  </Field>
                </div>
                <div style={{ fontSize: 12, color: '#888', margin: '4px 0 12px' }}>
                  应付金额：{currentTier ? `¥${currentTier.price}` : '—'}（可用代金券自动抵扣）
                </div>
                {activationCode && (
                  <Alert
                    type="success"
                    showIcon
                    style={{ marginBottom: 12 }}
                    data-testid="deploy-exe-activation-result"
                    message="激活码已签发"
                    description={
                      <Space orientation="vertical" size={4} style={{ width: '100%' }}>
                        <Text copyable style={{ color: '#7ecbff', wordBreak: 'break-all', fontSize: 12 }}>
                          {activationCode}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          请粘贴到查看器激活页（或已自动复制到剪贴板）
                        </Text>
                      </Space>
                    }
                  />
                )}
                <TierGateAlert artifact={artifact} policy={policy} label="购买激活码" testId="deploy-exe-buy-gate" />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="primary"
                    loading={buying}
                    disabled={!gateOk}
                    onClick={() => void doBuy()}
                    data-testid="deploy-exe-buy"
                  >
                    购买激活码
                  </Button>
                </div>
              </div>
            ),
          },
          {
            key: 'records',
            label: '购买记录',
            children: (
              <div data-testid="deploy-exe-records">
                <Table<DeployActivationRecord>
                  rowKey="id"
                  size="small"
                  loading={recordsLoading}
                  dataSource={records}
                  pagination={false}
                  locale={{ emptyText: '暂无已购买的激活码' }}
                  columns={[
                    { title: '机器码', dataIndex: 'machineCode', width: 180 },
                    {
                      title: '激活码',
                      dataIndex: 'activationCode',
                      render: (v: string | null, r) => (
                        <Space size={4}>
                          <Text style={{ fontSize: 12 }}>{maskCode(v)}</Text>
                          {v ? (
                            <Button
                              type="link"
                              size="small"
                              onClick={() => void navigator.clipboard?.writeText(v).then(() => message.success('已复制'))}
                            >
                              复制
                            </Button>
                          ) : null}
                          <Text type="secondary" style={{ fontSize: 11 }}>{r.features?.filter((f) => f.startsWith('deploy:')).join('')}</Text>
                        </Space>
                      ),
                    },
                    {
                      title: '到期时间',
                      dataIndex: 'expiresAt',
                      width: 170,
                      render: (v: string | null) => (v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '长期'),
                    },
                    {
                      title: '状态',
                      width: 90,
                      render: (_: unknown, r) =>
                        r.revoked ? (
                          <Tag color="red">已吊销</Tag>
                        ) : r.expiresAt && new Date(r.expiresAt).getTime() < Date.now() ? (
                          <Tag>已过期</Tag>
                        ) : (
                          <Tag color="green">有效</Tag>
                        ),
                    },
                    {
                      title: '操作',
                      width: 90,
                      render: (_: unknown, r) =>
                        r.revoked ? null : (
                          <Popconfirm title="确认吊销该激活码？" onConfirm={() => void revoke(r.id)} okText="吊销" cancelText="取消">
                            <Button type="link" size="small" danger data-testid="deploy-exe-revoke">
                              吊销
                            </Button>
                          </Popconfirm>
                        ),
                    },
                  ]}
                />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}

/** 表单行：label + 问标（悬浮显示说明+用例） */
function Field({
  label,
  tip,
  children,
  span2,
}: {
  label: string;
  tip: string;
  children: React.ReactNode;
  span2?: boolean;
}) {
  return (
    <div style={{ gridColumn: span2 ? '1 / -1' : undefined }}>
      <div style={{ marginBottom: 4 }}>
        <Text style={{ fontSize: 13 }}>{label}</Text>
        <Tooltip title={tip}>
          <span style={{ marginLeft: 6, color: '#666', cursor: 'help' }}>?</span>
        </Tooltip>
      </div>
      {children}
    </div>
  );
}
